import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ArrowLeft } from "lucide-react";
import SectionHeader from "../components/common/SectionHeader";
import OpportunityRow from "../components/opportunity/OpportunityRow";
import OpportunityBlock from "../components/opportunity/OpportunityBlock";
import OpportunityDetail from "../components/opportunity/OpportunityDetail";
import EmptyState from "../components/common/EmptyState";
import { useOpportunities } from "../hooks/data";
import { WALLET_BLOCKS, classifyIntoBlocks, sortForWallet } from "../lib/walletSegments";
import { fmtMoney } from "../lib/format";
import { T } from "../lib/testids";

const BLOCK_PAGE_SIZE = 3;
const ALL_PAGE_SIZE = 50;

/**
 * Mi cartera — Ronda 2.1: reemplaza la tabla larga (incluso con pestañas y
 * paginación, con ~48 registros seguía sintiéndose como una sábana) por
 * bloques comerciales que responden "a quién atendería primero", cada uno
 * con pocas filas. "Ver toda mi cartera" es la única puerta a la tabla
 * completa — ya no hay pestañas: eran exactamente redundantes frente a los
 * bloques (auditoría Ronda 2.1).
 */
export default function Wallet() {
  // Deep-link desde Home ("Empezaría por aquí" / "Después, seguiría con"):
  // llega con la oportunidad ya elegida, nunca a una lista vacía que haya
  // que buscar.
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState("blocks"); // "blocks" | "all"
  const [search, setSearch] = useState("");
  const [blockVisible, setBlockVisible] = useState({ resume: BLOCK_PAGE_SIZE, expiring: BLOCK_PAGE_SIZE, high_priority: BLOCK_PAGE_SIZE });
  const [allVisibleCount, setAllVisibleCount] = useState(ALL_PAGE_SIZE);
  const [selectedId, setSelectedId] = useState(() => searchParams.get("opp") || null);

  // Límite conocido, documentado, sin resolver todavía (auditoría UX de
  // escalabilidad, agosto 2026): con carteras de 5,000-50,000 oportunidades
  // esto truncaría en 500 sin avisar. Resolverlo requiere paginación real
  // en el servidor (GET /api/storage/opportunities hoy solo acepta
  // engineId/priority, sin offset/cursor) — deliberadamente fuera de esta
  // ronda, que es solo presentación.
  const { data: opps, loading, setData } = useOpportunities({ limit: 500 });

  const searched = useMemo(() => {
    if (!search) return opps;
    const s = search.toLowerCase();
    return opps.filter((o) => o.companyName.toLowerCase().includes(s) || o.contactName.toLowerCase().includes(s));
  }, [opps, search]);

  const blocks = useMemo(() => {
    const raw = classifyIntoBlocks(searched);
    return {
      resume: sortForWallet(raw.resume),
      expiring: sortForWallet(raw.expiring),
      high_priority: sortForWallet(raw.high_priority),
      rest: sortForWallet(raw.rest),
    };
  }, [searched]);

  const totalOpenMatching = blocks.resume.length + blocks.expiring.length + blocks.high_priority.length + blocks.rest.length;

  const allSorted = useMemo(() => sortForWallet(searched), [searched]);

  const potentialValue = useMemo(
    () =>
      [...blocks.resume, ...blocks.expiring, ...blocks.high_priority, ...blocks.rest].reduce(
        (s, o) => s + o.estimatedValue.value.amount,
        0
      ),
    [blocks]
  );

  // Cada vez que cambia la búsqueda, "Ver más" de cada bloque y de la tabla
  // completa vuelven a su tope inicial — nunca dejan un "Ver más" apuntando
  // a un conjunto que ya no aplica.
  useEffect(() => {
    setBlockVisible({ resume: BLOCK_PAGE_SIZE, expiring: BLOCK_PAGE_SIZE, high_priority: BLOCK_PAGE_SIZE });
    setAllVisibleCount(ALL_PAGE_SIZE);
  }, [search]);

  const visibleAll = allSorted.slice(0, allVisibleCount);
  const remainingAll = allSorted.length - visibleAll.length;

  const handleUpdated = (u) => setData((prev) => prev.map((o) => (o.id === u.id ? u : o)));

  return (
    <div className="ambient-bg min-h-full">
      <div className="px-4 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-white/[0.06]">
        <SectionHeader
          overline="Mi cartera"
          title="Tu cartera"
          right={
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                Potencial de ingreso
              </div>
              <div className="font-display font-black text-3xl tabular-nums text-amber-400 mt-1">
                {/* "USD 0" no comunica nada útil cuando SÍ hay oportunidades
                    pero sus valores todavía no se calculan (mismo criterio
                    que AuditableFigure, Regla #3). */}
                {totalOpenMatching > 0 && potentialValue === 0
                  ? <span className="text-lg italic text-zinc-500 font-display font-bold">Lo estoy calculando</span>
                  : fmtMoney(potentialValue)}
              </div>
            </div>
          }
        />
        {/* La instrucción de esta pantalla — no un subtítulo secundario.
            Mismo tratamiento que ya usa el párrafo de bienvenida de Home
            (text-base sm:text-lg text-zinc-300), en vez del subtítulo
            genérico compartido con Cierres/Ejecutivo, que a 14px gris
            apagado casi desaparecía (hallazgo real, revisión de Fernando
            dentro del iframe de GHL). */}
        <p className="text-base sm:text-lg text-zinc-300 leading-relaxed max-w-2xl mt-3">
          Esto es lo que atendería primero.
        </p>
      </div>

      <div className="px-4 sm:px-8 py-4 border-b border-white/[0.06] bg-[#0d1321] flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        {viewMode === "all" ? (
          <button
            data-testid={T.walletBackToPriority}
            onClick={() => setViewMode("blocks")}
            className="inline-flex items-center gap-1.5 text-[13px] text-zinc-400 hover:text-amber-400 border-b border-transparent hover:border-amber-400 pb-0.5 transition-colors"
          >
            <ArrowLeft size={12} /> Volver a lo prioritario
          </button>
        ) : (
          <button
            data-testid={T.walletViewAll}
            onClick={() => setViewMode("all")}
            className="inline-flex items-center gap-1.5 text-[13px] text-zinc-400 hover:text-amber-400 border-b border-transparent hover:border-amber-400 pb-0.5 transition-colors"
          >
            Ver toda mi cartera ({opps.length})
          </button>
        )}
        <div className="relative w-full sm:w-auto sm:ml-auto sm:min-w-[180px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            data-testid={T.walletSearch}
            placeholder="Buscar cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg bg-white/[0.04] border border-white/[0.07] text-base sm:text-xs text-zinc-200 pl-7 pr-2 py-1.5 focus:outline-none focus:border-amber-500/60 transition-colors"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_460px] min-h-[calc(100vh-260px)]">
        <div className="border-r border-white/[0.06]">
          {loading && (
            <div className="px-6 py-10 text-center text-zinc-500 font-mono text-[11px]">Cargando...</div>
          )}

          {!loading && viewMode === "blocks" && totalOpenMatching === 0 && (
            <EmptyState
              title="No hay oportunidades pendientes ahora mismo"
              description={search ? "Nadie coincide con esa búsqueda." : "Buen momento para revisar tu cartera completa o cargar nuevos clientes."}
            />
          )}

          {!loading && viewMode === "blocks" && totalOpenMatching > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid={T.walletTable}>
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono border-b border-white/[0.06]">
                      <th className="px-6 py-2.5 font-normal">Valor estimado</th>
                      <th className="px-4 py-2.5 font-normal w-36">Tiempo para actuar</th>
                      <th className="px-4 py-2.5 font-normal">Cuenta</th>
                      <th className="px-4 py-2.5 font-normal w-20">Prioridad</th>
                      <th className="px-4 py-2.5 font-normal">Respuesta</th>
                      <th className="px-4 py-2.5 font-normal">Qué encontramos</th>
                      <th className="px-6 py-2.5 font-normal">Resultado</th>
                    </tr>
                  </thead>
                  {WALLET_BLOCKS.filter((b) => b.key !== "rest").map((b) => (
                    <OpportunityBlock
                      key={b.key}
                      label={b.label}
                      opportunities={blocks[b.key]}
                      visibleCount={blockVisible[b.key]}
                      onShowMore={() => setBlockVisible((v) => ({ ...v, [b.key]: v[b.key] + BLOCK_PAGE_SIZE }))}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      testid={T.walletBlock(b.key)}
                    />
                  ))}
                </table>
              </div>

              {/* "El resto de tu cartera" no es una lista — es la parte de
                  la cartera que hoy no exige nada de ti. Mostrar 2-3 filas
                  ahí no ayuda a decidir nada (no hay decisión que tomar en
                  este bloque, por definición) — solo un resumen y la
                  puerta a la cartera completa. */}
              {blocks.rest.length > 0 && (
                <div
                  data-testid={T.walletRest}
                  className="mx-4 sm:mx-6 my-5 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-5 py-4 flex items-center justify-between gap-4 flex-wrap"
                >
                  <div>
                    <div className="text-sm text-zinc-200 font-medium">
                      El resto de tu cartera — {blocks.rest.length} oportunidades
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">Sin acción urgente por ahora.</div>
                  </div>
                  <button
                    onClick={() => setViewMode("all")}
                    className="text-[13px] text-zinc-400 hover:text-amber-400 transition-colors shrink-0"
                  >
                    Ver toda mi cartera →
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && viewMode === "all" && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid={T.walletTable}>
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono border-b border-white/[0.06]">
                    <th className="px-6 py-2.5 font-normal">Valor estimado</th>
                    <th className="px-4 py-2.5 font-normal w-36">Tiempo para actuar</th>
                    <th className="px-4 py-2.5 font-normal">Cuenta</th>
                    <th className="px-4 py-2.5 font-normal w-20">Prioridad</th>
                    <th className="px-4 py-2.5 font-normal">Respuesta</th>
                    <th className="px-4 py-2.5 font-normal">Qué encontramos</th>
                    <th className="px-6 py-2.5 font-normal">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAll.map((o) => (
                    <OpportunityRow
                      key={o.id}
                      opp={o}
                      selected={selectedId === o.id}
                      onClick={() => setSelectedId(o.id)}
                      testid={T.walletRow(o.id)}
                    />
                  ))}
                  {!visibleAll.length && (
                    <tr><td colSpan={7} className="px-6 py-10 text-center text-zinc-500 font-mono text-[11px]">Sin resultados</td></tr>
                  )}
                </tbody>
              </table>
              {remainingAll > 0 && (
                <div className="px-6 py-5 flex justify-center">
                  <button
                    data-testid={T.walletLoadMore}
                    onClick={() => setAllVisibleCount((c) => c + ALL_PAGE_SIZE)}
                    className="text-[11px] font-mono uppercase tracking-[0.15em] px-4 py-2 rounded-full border border-white/[0.1] text-zinc-300 hover:border-amber-500/60 hover:text-amber-400 transition-colors"
                  >
                    Mostrar más — {remainingAll} restantes
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-[#0d1321]">
          <OpportunityDetail opportunityId={selectedId} onUpdated={handleUpdated} />
        </div>
      </div>
    </div>
  );
}
